import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import LinkEmailPromptModal from "../link-email-prompt-modal";
import { useAuth } from "../../context/authContext";
import { usePlatform } from "../../hooks/usePlatform";

const PROMPT_STORAGE_KEY = "openway:link-email-prompt";
const SESSION_DISMISS_KEY = "openway:link-email-prompt-session";
const RETRY_AFTER_MS = 5 * 24 * 60 * 60 * 1000;
const MAX_DISMISSALS = 2;

type PromptStorage = {
  dismissCount: number;
  lastDismissedAt: number;
};

function isEmailLinked(user: { linkedProviders: { provider: string }[] } | null): boolean {
  return user?.linkedProviders.some((p) => p.provider === "email") ?? false;
}

function readPromptStorage(): PromptStorage | null {
  try {
    const raw = localStorage.getItem(PROMPT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PromptStorage;
    if (
      typeof parsed.dismissCount !== "number" ||
      typeof parsed.lastDismissedAt !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writePromptStorage(state: PromptStorage): void {
  try {
    localStorage.setItem(PROMPT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

/** Показать сразу (1-й раз) или повторно через 5 дней после «В следующий раз»; не более 2 показов. */
function shouldShowLinkEmailPrompt(): boolean {
  const state = readPromptStorage();
  if (!state) return true;
  if (state.dismissCount >= MAX_DISMISSALS) return false;
  if (state.dismissCount === 1) {
    return Date.now() - state.lastDismissedAt >= RETRY_AFTER_MS;
  }
  return true;
}

function recordPromptDismissed(): void {
  const prev = readPromptStorage();
  writePromptStorage({
    dismissCount: (prev?.dismissCount ?? 0) + 1,
    lastDismissedAt: Date.now(),
  });
}

function isDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function isLinkEmailFlowPath(pathname: string): boolean {
  return pathname.startsWith("/account/login-methods/link-email");
}

/**
 * В Telegram Mini App показывает bottom sheet, если email не привязан.
 * На обычном сайте не показываем: вход всегда по email, провайдер уже есть.
 */
function LinkEmailPromptGate() {
  const { status, user } = useAuth();
  const { isTelegram } = usePlatform();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const emailLinked = useMemo(() => isEmailLinked(user), [user]);

  const canPrompt = useMemo(() => {
    if (!isTelegram) return false;
    if (status !== "authed" || !user) return false;
    if (emailLinked) return false;
    if (!shouldShowLinkEmailPrompt()) return false;
    if (isDismissedThisSession()) return false;
    if (isLinkEmailFlowPath(location.pathname)) return false;
    return true;
  }, [isTelegram, status, user, emailLinked, location.pathname]);

  useEffect(() => {
    setOpen(canPrompt);
  }, [canPrompt]);

  const handleDismiss = useCallback(() => {
    recordPromptDismissed();
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setOpen(false);
    navigate("/account/login-methods/link-email");
  }, [navigate]);

  return (
    <LinkEmailPromptModal
      open={open}
      onClose={handleDismiss}
      onConfirm={handleConfirm}
    />
  );
}

export default LinkEmailPromptGate;
